import React from "react"
import ReactDOM from "react-dom/client"
import { RouterProvider } from "@tanstack/react-router"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { router } from "./router"
import { initSentry, SentryErrorFallback } from '@shared/lib/sentry'
import * as Sentry from '@sentry/react'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { useAdminAuthStore } from '@/domains/admin/stores'
import { createDomainAuthUtils } from '@shared/lib/auth-utils'
import { initImmigrationDomain } from '@/domains/immigration/init'
import { initFingerprintingDomain } from '@/domains/fingerprinting/init'
import "./globals.css"

// Initialize Sentry before anything else
initSentry()

// Initialize domain-specific configurations
initImmigrationDomain()
initFingerprintingDomain()

const adminAuthUtils = createDomainAuthUtils('admin')

// Validate auth state on app initialization
const validateAuthState = () => {
  const store = useAdminAuthStore.getState()
  const isTokenValid = adminAuthUtils.isAuthenticated()
  
  if (store.isAuthenticated && !isTokenValid) {
    console.log('Auth state mismatch detected: clearing stale auth data')
    adminAuthUtils.clearAuth()
    store.handleAuthError()
  }
}

validateAuthState()

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 1000 * 60 * 5, // 5 minutes
            refetchOnWindowFocus: false,
            retry: (failureCount, error: any) => {
                // Don't retry authentication errors
                if (error?.response?.errorCode === 'UNAUTHORIZED' || 
                    error?.response?.statusCode === 401 ||
                    error?.message === 'Token expired') {
                    return false
                }
                // Don't retry permission errors - they won't succeed without proper permissions
                if (error?.response?.errorCode === 'FORBIDDEN' || 
                    error?.response?.statusCode === 403 ||
                    error?.response?.retryable === false ||
                    error?.statusCode === 403 ||
                    error?.message?.toLowerCase().includes('forbidden') ||
                    error?.message?.toLowerCase().includes('access denied') ||
                    error?.message?.toLowerCase().includes('insufficient permissions')) {
                    console.log('Preventing retry for permission error:', error?.message || error?.response?.message)
                    return false
                }
                return failureCount < 1
            },
        },
        mutations: {
            retry: (failureCount, error: any) => {
                // Don't retry authentication errors
                if (error?.response?.errorCode === 'UNAUTHORIZED' || 
                    error?.response?.statusCode === 401 ||
                    error?.message === 'Token expired') {
                    return false
                }
                // Don't retry permission errors - they won't succeed without proper permissions
                if (error?.response?.errorCode === 'FORBIDDEN' || 
                    error?.response?.statusCode === 403 ||
                    error?.response?.retryable === false ||
                    error?.statusCode === 403 ||
                    error?.message?.toLowerCase().includes('forbidden') ||
                    error?.message?.toLowerCase().includes('access denied') ||
                    error?.message?.toLowerCase().includes('insufficient permissions')) {
                    console.log('Preventing retry for permission error:', error?.message || error?.response?.message)
                    return false
                }
                return failureCount < 1
            },
        },
    },
})

// Expose queryClient globally for cache clearing during logout
if (typeof window !== 'undefined') {
    (window as any).__QUERY_CLIENT__ = queryClient
}

// Set up global error handling for React Query
queryClient.setDefaultOptions({
    queries: {
        staleTime: 1000 * 60 * 5, // 5 minutes
        refetchOnWindowFocus: false,
    },
    mutations: {
        retry: (failureCount, error: any) => {
            // Don't retry authentication errors
            if (error?.response?.errorCode === 'UNAUTHORIZED' || 
                error?.response?.statusCode === 401 ||
                error?.message === 'Token expired') {
                return false
            }
            // Don't retry permission errors - they won't succeed without proper permissions
            if (error?.response?.errorCode === 'FORBIDDEN' || 
                error?.response?.statusCode === 403 ||
                error?.response?.retryable === false ||
                error?.statusCode === 403 ||
                error?.message?.toLowerCase().includes('forbidden') ||
                error?.message?.toLowerCase().includes('access denied') ||
                error?.message?.toLowerCase().includes('insufficient permissions')) {
                console.log('Preventing retry for permission error:', error?.message || error?.response?.message)
                return false
            }
            return failureCount < 1
        },
    }
})

async function enableMocking() {
  if (import.meta.env.VITE_ENABLE_MOCK_API !== 'true') {
    return
  }

  const { worker } = await import('./mocks/browser')
  return worker.start()
}

enableMocking().then(() =>
  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
        <Sentry.ErrorBoundary 
            fallback={(errorData) => <SentryErrorFallback error={errorData.error as Error} resetError={errorData.resetError} />}
        >
            <QueryClientProvider client={queryClient}>
                <ReactQueryDevtools />
                <RouterProvider router={router} />
            </QueryClientProvider>
        </Sentry.ErrorBoundary>
    </React.StrictMode>,
))
